import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Alert,
  ListGroup,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import OperationProgress from '../components/OperationProgress';
import { useCopyProgress } from '../hooks/useCopyProgress';
import MissingSelectOption from '../components/MissingSelectOption';
import {
  validateMeterReadingForm,
  validateField,
  ValidationRules,
  hasErrors,
  formatErrors,
} from '../utils/validation';

function emptyForm() {
  return {
    fleet_no: '',
    reading_date: '',
    meter: '',
    fuel_stock_no: '',
    fuel_qty: '',
    fuel_unit_cost: '',
  };
}

function toForm(record) {
  if (!record) {
    return emptyForm();
  }
  return {
    fleet_no: record.fleet_no || '',
    reading_date: record.reading_date ? String(record.reading_date).split('T')[0] : '',
    meter: record.meter ?? '',
    fuel_stock_no: record.fuel_stock_no || '',
    fuel_qty: record.fuel_qty ?? '',
    fuel_unit_cost: record.fuel_unit_cost ?? '',
  };
}

const SYSTEM_FIELDS = new Set(['id', 'created_at', 'updated_at', 'tenant_id', 'user_id']);

const GRID_FIELD_LABELS = {
  reading_date: 'Reading date',
  meter: 'Meter',
  fuel_qty: 'Fuel quantity',
  fuel_unit_cost: 'Fuel unit cost',
};

function buildRecordPayload(record, overrides = {}) {
  const payload = {};
  for (const [key, value] of Object.entries(record)) {
    if (!SYSTEM_FIELDS.has(key)) {
      payload[key] = value;
    }
  }
  return { ...payload, ...overrides };
}

function todayIsoDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDaysToIsoDate(isoDate, days) {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

function buildCopyPayload(record) {
  const payload = buildRecordPayload(record);
  const originalDate = payload.reading_date ? String(payload.reading_date).split('T')[0] : null;
  const today = todayIsoDate();
  const readingDate = originalDate === today ? addDaysToIsoDate(today, 1) : today;

  return {
    ...payload,
    reading_date: readingDate,
  };
}

function formatHireStockLabel(item) {
  if (!item) {
    return '';
  }
  return `${item.name} (${item.stock_no})`;
}

function formatGridEditValue(row, field) {
  if (field === 'reading_date') {
    return row.reading_date ? String(row.reading_date).split('T')[0] : '';
  }
  if (field === 'meter' || field === 'fuel_qty' || field === 'fuel_unit_cost') {
    return row[field] == null || row[field] === '' ? '' : String(row[field]);
  }
  return row[field] == null ? '' : String(row[field]);
}

function formatGridDisplayValue(row, field, formatDate) {
  if (field === 'reading_date') {
    return formatDate(row.reading_date);
  }
  if (field === 'meter' || field === 'fuel_qty' || field === 'fuel_unit_cost') {
    return row[field] ?? '—';
  }
  return row[field] || '—';
}

function parseGridSaveValue(field, raw) {
  if (field === 'reading_date') {
    return raw || null;
  }
  if (field === 'meter' || field === 'fuel_qty' || field === 'fuel_unit_cost') {
    return raw === '' ? null : raw;
  }
  return raw.trim();
}

function gridValuesEqual(field, current, next) {
  if (field === 'meter') {
    const currentNum = current == null || current === '' ? null : Number(current);
    const nextNum = next == null || next === '' ? null : Number(next);
    if (currentNum === null && nextNum === null) {
      return true;
    }
    if (currentNum === null || nextNum === null || Number.isNaN(nextNum)) {
      return String(current ?? '') === String(next ?? '');
    }
    return currentNum === nextNum;
  }
  if (field === 'fuel_qty' || field === 'fuel_unit_cost') {
    const currentNum = current == null || current === '' ? null : Number(current);
    const nextNum = next == null || next === '' ? null : Number(next);
    if (currentNum === null && nextNum === null) {
      return true;
    }
    if (currentNum === null || nextNum === null || Number.isNaN(nextNum)) {
      return String(current ?? '') === String(next ?? '');
    }
    return currentNum === nextNum;
  }
  if (field === 'reading_date') {
    const currentDate = current ? String(current).split('T')[0] : '';
    const nextDate = next ? String(next).split('T')[0] : '';
    return currentDate === nextDate;
  }
  const currentText = current == null ? '' : String(current).trim();
  const nextText = next == null ? '' : String(next).trim();
  return currentText === nextText;
}

function EditableGridCell({
  row,
  field,
  type = 'text',
  step,
  editingCell,
  editingValue,
  savingCell,
  onStartEdit,
  onChange,
  onSave,
  onKeyDown,
  formatDate,
}) {
  const cellKey = `${row.id}-${field}`;
  const isEditing = editingCell === cellKey;
  const isSaving = savingCell === cellKey;
  const displayValue = formatGridDisplayValue(row, field, formatDate);

  if (isEditing) {
    return (
      <Form.Control
        type={type}
        size="sm"
        value={editingValue}
        step={step ?? (type === 'number' ? '0.01' : undefined)}
        min={type === 'number' ? '0' : undefined}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => onSave(row, field)}
        onKeyDown={(e) => onKeyDown(e, row, field)}
        autoFocus
        disabled={isSaving}
        style={{ minWidth: type === 'date' ? '140px' : '100px' }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      style={{ cursor: 'pointer', display: 'inline-block', minWidth: '48px' }}
      onClick={() => onStartEdit(row, field)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStartEdit(row, field);
        }
      }}
      title="Click to edit"
    >
      {displayValue}
    </span>
  );
}

function MeterReadingModal({ record, onClose, onSave }) {
  const [formData, setFormData] = useState(emptyForm());
  const [hireStockItems, setHireStockItems] = useState([]);
  const [salesItems, setSalesItems] = useState([]);
  const [fleetSearch, setFleetSearch] = useState('');
  const [showFleetMatches, setShowFleetMatches] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const token = localStorage.getItem('token');
        const [stockRes, salesRes] = await Promise.all([
          axios.get('/api/hire-stock/stock-numbers', {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get('/api/sales-items/dropdown'),
        ]);
        setHireStockItems(stockRes.data);
        setSalesItems(Array.isArray(salesRes.data) ? salesRes.data : []);
      } catch (error) {
        console.error('Failed to fetch lookup data:', error);
      }
    };
    fetchLookups();
  }, []);

  useEffect(() => {
    setFormData(toForm(record));
    setSaveError('');
    setValidationErrors({});
    setShowFleetMatches(false);
  }, [record]);

  useEffect(() => {
    const fleetNo = formData.fleet_no;
    if (!fleetNo) {
      setFleetSearch('');
      return;
    }
    const match = hireStockItems.find((item) => item.stock_no === fleetNo);
    setFleetSearch(match ? formatHireStockLabel(match) : fleetNo);
  }, [formData.fleet_no, hireStockItems, record]);

  const filteredHireStockItems = useMemo(() => {
    const query = fleetSearch.trim().toLowerCase();
    const items = query
      ? hireStockItems.filter((item) => {
          const code = (item.stock_no || '').toLowerCase();
          const name = (item.name || '').toLowerCase();
          return code.includes(query) || name.includes(query);
        })
      : hireStockItems;

    return items.slice(0, 20);
  }, [hireStockItems, fleetSearch]);

  const handleSelectFleetItem = (item) => {
    setFormData((prev) => ({ ...prev, fleet_no: item.stock_no }));
    setFleetSearch(formatHireStockLabel(item));
    setShowFleetMatches(false);
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.fleet_no;
      return next;
    });
    setSaveError('');
  };

  const handleFleetSearchChange = (value) => {
    setFleetSearch(value);
    setShowFleetMatches(true);

    const trimmed = value.trim().toLowerCase();
    const exactMatch = hireStockItems.find(
      (item) =>
        item.stock_no?.toLowerCase() === trimmed ||
        formatHireStockLabel(item).toLowerCase() === trimmed
    );

    setFormData((prev) => ({
      ...prev,
      fleet_no: exactMatch ? exactMatch.stock_no : '',
    }));

    if (validationErrors.fleet_no) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next.fleet_no;
        return next;
      });
    }
    setSaveError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setSaveError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateMeterReadingForm(formData);
    if (hasErrors(errors)) {
      setValidationErrors(errors);
      setSaveError(formatErrors(errors));
      return;
    }
    setValidationErrors({});
    setSaveError('');
    try {
      await onSave(formData);
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to save meter reading';
      setSaveError(message);
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>{record ? 'Edit Meter Reading' : 'Add Meter Reading'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {saveError && (
          <Alert variant="danger" className="mb-3">
            {saveError}
          </Alert>
        )}
        <Form noValidate onSubmit={handleSubmit}>
          <Container fluid>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3 position-relative">
                  <Form.Label>Fleet No *</Form.Label>
                  <Form.Control
                    type="search"
                    name="fleet_no_search"
                    placeholder="Search by hire stock name or number..."
                    value={fleetSearch}
                    onChange={(e) => handleFleetSearchChange(e.target.value)}
                    onFocus={() => setShowFleetMatches(true)}
                    onBlur={() => {
                      window.setTimeout(() => setShowFleetMatches(false), 150);
                    }}
                    autoComplete="off"
                    isInvalid={!!validationErrors.fleet_no}
                  />
                  {formData.fleet_no && (
                    <Form.Text className="text-muted">
                      Selected fleet no: {formData.fleet_no}
                    </Form.Text>
                  )}
                  {showFleetMatches && filteredHireStockItems.length > 0 && (
                    <ListGroup
                      className="position-absolute w-100 shadow-sm"
                      style={{ zIndex: 2, maxHeight: '220px', overflowY: 'auto' }}
                    >
                      {filteredHireStockItems.map((item) => (
                        <ListGroup.Item
                          key={item.stock_no}
                          action
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleSelectFleetItem(item)}
                        >
                          <div className="fw-medium">{item.name}</div>
                          <div className="small text-muted">Fleet no: {item.stock_no}</div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                  {showFleetMatches &&
                    fleetSearch.trim() &&
                    filteredHireStockItems.length === 0 && (
                      <div className="small text-muted mt-1">
                        No matching hire stock items found
                      </div>
                    )}
                  {validationErrors.fleet_no && (
                    <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                      {validationErrors.fleet_no}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Reading Date *</Form.Label>
                  <Form.Control
                    type="date"
                    name="reading_date"
                    value={formData.reading_date}
                    onChange={handleChange}
                    isInvalid={!!validationErrors.reading_date}
                  />
                  {validationErrors.reading_date && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.reading_date}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Meter</Form.Label>
                  <Form.Control
                    type="number"
                    step="1"
                    min="0"
                    name="meter"
                    value={formData.meter}
                    onChange={handleChange}
                    isInvalid={!!validationErrors.meter}
                  />
                  {validationErrors.meter && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.meter}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fuel Stock No</Form.Label>
                  <Form.Select
                    name="fuel_stock_no"
                    value={formData.fuel_stock_no}
                    onChange={handleChange}
                    isInvalid={!!validationErrors.fuel_stock_no}
                  >
                    <option value="">Select sales item (optional)...</option>
                    <MissingSelectOption
                      value={formData.fuel_stock_no}
                      options={salesItems}
                      getOptionValue={(item) => item.item_code}
                    />
                    {salesItems.map((item) => (
                      <option key={item.id} value={item.item_code}>
                        {item.item_name} ({item.item_code})
                      </option>
                    ))}
                  </Form.Select>
                  {validationErrors.fuel_stock_no && (
                    <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                      {validationErrors.fuel_stock_no}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Fuel Qty</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    name="fuel_qty"
                    value={formData.fuel_qty}
                    onChange={handleChange}
                    isInvalid={!!validationErrors.fuel_qty}
                  />
                  {validationErrors.fuel_qty && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.fuel_qty}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Fuel Unit Cost</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    name="fuel_unit_cost"
                    value={formData.fuel_unit_cost}
                    onChange={handleChange}
                    isInvalid={!!validationErrors.fuel_unit_cost}
                  />
                  {validationErrors.fuel_unit_cost && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.fuel_unit_cost}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Container>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {record ? 'Update' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function MeterReadings() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const { copyProgress, wrapCopy } = useCopyProgress();
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const fetchRecords = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/meter-readings', {
        params: { page, limit: pagination.limit, search },
      });
      setRecords(response.data.meterReadings || []);
      setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
    } catch (err) {
      setError(`Failed to fetch meter readings: ${err.message}`);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecords(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchRecords(newPage, searchTerm);
  };

  const handleSave = async (payload) => {
    try {
      if (editingRecord) {
        await axios.put(`/api/meter-readings/${editingRecord.id}`, payload);
        setSuccess('Meter reading updated successfully');
      } else {
        await axios.post('/api/meter-readings', payload);
        setSuccess('Meter reading created successfully');
      }
      setShowModal(false);
      fetchRecords(pagination.page, searchTerm);
    } catch (err) {
      throw err;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }
    try {
      await axios.delete(`/api/meter-readings/${id}`);
      setSuccess('Record deleted successfully');
      fetchRecords(pagination.page, searchTerm);
    } catch {
      setError('Failed to delete record');
    }
  };

  const startGridEdit = (row, field) => {
    setEditingCell(`${row.id}-${field}`);
    setEditingValue(formatGridEditValue(row, field));
  };

  const cancelGridEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const saveGridEdit = async (row, field) => {
    const cellKey = `${row.id}-${field}`;
    if (editingCell !== cellKey) {
      return;
    }

    const parsed = parseGridSaveValue(field, editingValue);
    if (gridValuesEqual(field, row[field], parsed)) {
      cancelGridEdit();
      return;
    }

    const fieldError = validateField(field, parsed, ValidationRules.meterReading[field], row);
    if (fieldError) {
      setError(fieldError);
      cancelGridEdit();
      return;
    }

    setSavingCell(cellKey);
    try {
      const response = await axios.put(`/api/meter-readings/${row.id}`, { [field]: parsed });
      setRecords((prev) =>
        prev.map((record) => (record.id === row.id ? { ...record, ...response.data } : record))
      );
      setSuccess(`${GRID_FIELD_LABELS[field] || 'Field'} updated`);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update record');
    } finally {
      setSavingCell(null);
      cancelGridEdit();
    }
  };

  const handleGridKeyDown = (e, row, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveGridEdit(row, field);
    } else if (e.key === 'Escape') {
      cancelGridEdit();
    }
  };

  const handleCopy = async (record) => {
    setCopyingId(record.id);
    setError('');
    try {
      await wrapCopy('Copying meter reading...', 1, async (tick) => {
        await axios.post('/api/meter-readings', buildCopyPayload(record));
        tick({ success: true });
      });
      setSuccess('Meter reading copied successfully');
      fetchRecords(pagination.page, searchTerm);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to copy meter reading');
    } finally {
      setCopyingId(null);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL meter readings?')) {
      return;
    }
    if (
      !window.confirm(
        'This will permanently delete all meter reading records. Are you absolutely sure?'
      )
    ) {
      return;
    }
    try {
      const response = await axios.delete('/api/meter-readings');
      setSuccess(`All records deleted successfully (${response.data.deletedCount || 0} records)`);
      fetchRecords(1, '');
    } catch {
      setError('Failed to delete all records');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex align-items-center">
            <i className="fas fa-tachometer-alt me-3 text-info" style={{ fontSize: '2.5rem' }}></i>
            <div>
              <h1 className="mb-0">Meter Readings</h1>
              <p className="text-muted mb-0">Record fleet meter and fuel readings</p>
            </div>
          </div>
        </Col>
        <Col xs="auto" style={{ position: 'relative', zIndex: 1 }}>
          <Button
            variant="primary"
            onClick={() => {
              setEditingRecord(null);
              setShowModal(true);
            }}
            className="me-2"
          >
            Add Record
          </Button>
          <Button variant="danger" onClick={handleDeleteAll} disabled={records.length === 0}>
            Delete All
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {copyProgress && (
        <OperationProgress
          title={copyProgress.title}
          processed={copyProgress.processed}
          total={copyProgress.total}
          success={copyProgress.success}
          failed={copyProgress.failed}
          variant="info"
        />
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search by fleet no or fuel stock no..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="text-center p-3">Loading...</div>
          ) : records.length === 0 ? (
            <div className="text-center p-3">No records found.</div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped bordered hover className="mb-3">
                  <thead>
                    <tr>
                      <th>Fleet No</th>
                      <th>Reading Date</th>
                      <th>Meter</th>
                      <th>Fuel Stock No</th>
                      <th>Fuel Qty</th>
                      <th>Fuel Unit Cost</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <strong>{row.fleet_no}</strong>
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="reading_date"
                            type="date"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="meter"
                            type="number"
                            step="1"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>{row.fuel_stock_no || '—'}</td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="fuel_qty"
                            type="number"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="fuel_unit_cost"
                            type="number"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                setEditingRecord(row);
                                setShowModal(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleCopy(row)}
                              disabled={copyingId === row.id}
                            >
                              {copyingId === row.id ? 'Copying...' : 'Copy'}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(row.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <GridPagination
                page={pagination.page}
                pages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <MeterReadingModal
          record={editingRecord}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </Container>
  );
}

export default MeterReadings;
